import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';

import graphicPaper from '@/assets/figma/accounts/3d-graphic-paper.png';
import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { getRelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import { ErrorState } from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useUIStore } from '@/stores/uiStore';

import { AccountDetail } from './components/AccountDetail';
import { AccountList } from './components/AccountList';

/**
 * 상단 1차 범위 배너 (Figma banner/앱 전용 기능 안내 495:6551) — 닫으면 브라우저에 기억한다.
 * 배경 #111 · r14는 이 배너만의 값이라 토큰(grey-900 · rounded-16)에 가장 가까운 값으로 맞춘다.
 */
const ScopeBanner = () => {
  const dismissed = useUIStore((s) => s.scopeBannerDismissed);
  const dismiss = useUIStore((s) => s.dismissScopeBanner);
  if (dismissed) return null;
  return (
    <div className="flex items-center gap-3.5 rounded-16 bg-grey-900 px-6 py-4">
      <div className="flex min-w-0 flex-col gap-[3px]">
        <p className="text-body-15-sb text-fg-white">
          웹에서는 일부 기능만 이용할 수 있어요
        </p>
        <p className="text-body-13-r text-fg-white-secondary">
          거래처 조회 · 처방 등록 · 실적 · 의약품 검색까지 지원합니다. 그 외
          기능은 앱에서 이용해 주세요.
        </p>
      </div>
      <Button
        variant="text"
        size="small"
        onClick={dismiss}
        className="ml-auto text-fg-white hover:bg-white/10 active:bg-white/20"
        leftIcon={<Icon name="cancel-line-16" />}
        aria-label="배너 닫기"
      />
    </div>
  );
};

/** 데스크톱(≥1024)에서만 첫 거래중 병의원을 자동 선택한다. 모바일은 목록이 첫 화면이다 */
const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

/**
 * M-04 · M-05 거래처 (Figma 485:4643 · 840:4503) — 좌측 360(전체 내역 메뉴 + 목록) + 우측 상세 카드.
 * 모바일(<1024)은 목록 → 상세 2단계: 상세가 선택되면 목록을 숨긴다.
 * 앱 전용 [+ 거래처 신청] 버튼은 v1.0 디자인에서 뺐다.
 * API: GET /web/business-relationships, GET /web/business-relationships/{relationshipId}
 */
export const AccountsPage = () => {
  const { relationshipId: relationshipIdParam } = useParams({
    strict: false,
  }) as { relationshipId?: string };
  const relationshipId = relationshipIdParam
    ? Number(relationshipIdParam)
    : undefined;
  const hasSelection = !!relationshipId;
  const navigate = useNavigate();
  const list = useBusinessRelationshipList();
  const items = list.data?.items;

  // /accounts로 들어오면 첫 거래중 병의원(없으면 첫 항목)을 선택한다 — 새로고침 · 공유 시 URL로 유지
  useEffect(() => {
    if (hasSelection || !items?.length || !isDesktop()) return;
    const first =
      items.find((i) => getRelationshipGroup(i.statusView) === 'ongoing') ??
      items[0];
    void navigate({
      to: ROUTE_PATHS.ACCOUNT_DETAIL,
      params: { relationshipId: String(first.relationshipId) },
      replace: true,
    });
  }, [hasSelection, items, navigate]);

  return (
    <div className="flex flex-col gap-6">
      <h1 className="sr-only">거래처</h1>
      <ScopeBanner />
      <div className="flex items-stretch gap-4">
        <div
          className={cn(
            'w-full shrink-0 flex-col gap-4 lg:flex lg:w-[360px]',
            hasSelection ? 'hidden' : 'flex',
          )}
        >
          {/* 전체 처방 · 정산 내역 보기 (Figma menu 495:6546) */}
          <Link
            to={ROUTE_PATHS.PERFORMANCE}
            className="flex items-center gap-4 rounded-20 bg-surface-white px-6 py-4 transition-colors outline-none hover:bg-grey-100 focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]"
          >
            <img src={graphicPaper} alt="" className="size-10 shrink-0" />
            <span className="text-subtitle-18-b text-fg-primary">
              전체 처방 · 정산 내역 보기
            </span>
          </Link>
          <section className="flex-1 rounded-20 bg-surface-white p-6">
            {list.isLoading && (
              <div className="flex flex-col gap-4">
                <Skeleton className="h-6 w-16 rounded-8" />
                <Skeleton className="h-12 rounded-16" />
                <Skeleton className="h-12 rounded-16" />
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-20 rounded-20" />
                ))}
              </div>
            )}
            {list.isError && <ErrorState onRetry={() => list.refetch()} />}
            {items && (
              <AccountList
                items={items}
                counts={list.data?.counts}
                selectedId={relationshipId}
              />
            )}
          </section>
        </div>
        <section
          className={cn(
            'min-w-0 flex-1 flex-col lg:flex',
            hasSelection ? 'flex' : 'hidden',
          )}
        >
          {relationshipId && (
            // 거래처를 바꾸면 드로어 · 오버레이 상태를 새로 시작한다
            <AccountDetail
              key={relationshipId}
              relationshipId={relationshipId}
            />
          )}
        </section>
      </div>
    </div>
  );
};
